import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import data from '../data/data.json';
import MagneticButton from './MagneticButton';

function RotatingText({ items }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 3200);
    return () => clearInterval(timer);
  }, [items.length]);

  return (
    <div style={{ position: 'relative', height: '1.4em', overflow: 'hidden', display: 'inline-block', verticalAlign: 'bottom' }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={items[index]}
          initial={{ y: 28, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -28, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          style={{
            display: 'inline-block',
            color: 'var(--accent)',
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
          }}
        >
          {items[index]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

function BioTechPortal({ mousePos }) {
  const [photoError, setPhotoError] = useState(false);

  // Subtle interactive 3D parallax tilt based on mouse
  const tiltX = (mousePos.y - 0.5) * 12;
  const tiltY = (mousePos.x - 0.5) * -12;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '440px',
        margin: '0 auto',
        aspectRatio: '1 / 1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        perspective: '1000px',
      }}
    >
      <motion.div
        animate={{
          rotateX: tiltX,
          rotateY: tiltY,
        }}
        transition={{ type: 'spring', stiffness: 120, damping: 18 }}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Ambient background bloom */}
        <div
          style={{
            position: 'absolute',
            inset: '10%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(247, 190, 161, 0.4) 0%, rgba(232, 149, 109, 0.15) 50%, transparent 75%)',
            filter: 'blur(35px)',
            zIndex: 0,
          }}
        />

        {/* Decorative Orbital Ring 1 */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            inset: '4%',
            borderRadius: '50%',
            border: '1px dashed rgba(232, 149, 109, 0.35)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '12%',
              left: '12%',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'var(--accent)',
              boxShadow: '0 0 10px var(--accent)',
            }}
          />
        </motion.div>

        {/* Decorative Orbital Ring 2 */}
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 55, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            inset: '-2%',
            borderRadius: '50%',
            border: '1px solid rgba(45, 23, 83, 0.08)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: '18%',
              right: '18%',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: 'var(--text)',
              opacity: 0.4,
            }}
          />
        </motion.div>

        {/* Morphing Organic Glass Frame */}
        <motion.div
          animate={{
            borderRadius: [
              '60% 40% 30% 70% / 60% 30% 70% 40%',
              '40% 60% 70% 30% / 50% 60% 40% 60%',
              '55% 45% 40% 60% / 40% 50% 50% 60%',
              '60% 40% 30% 70% / 60% 30% 70% 40%',
            ],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'relative',
            width: '76%',
            height: '76%',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.6) 0%, rgba(247, 190, 161, 0.25) 100%)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1.5px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 20px 50px rgba(45, 23, 83, 0.12), inset 0 2px 6px rgba(255, 255, 255, 0.9)',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          {!photoError ? (
            <img
              src="/images/darshni-photo.png"
              alt="Darshni S"
              onError={() => setPhotoError(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top center',
                mixBlendMode: 'multiply',
                filter: 'contrast(1.04) brightness(1.02)',
                display: 'block',
                transition: 'transform 0.5s ease',
              }}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text)',
                textAlign: 'center',
                padding: '1.5rem',
              }}
            >
              <span style={{ fontSize: '3.5rem', fontFamily: 'var(--font-heading)', fontWeight: 700, color: 'var(--accent)' }}>
                D·S
              </span>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Healthcare Tech & AI
              </span>
            </div>
          )}
        </motion.div>

        {/* Floating Bio-Tech Floating Chips */}
        {/* Chip 1: Biosignals */}
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            top: '8%',
            right: '-4%',
            background: 'rgba(250, 246, 238, 0.92)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(45, 23, 83, 0.12)',
            boxShadow: '0 8px 24px rgba(45, 23, 83, 0.08)',
            borderRadius: '100px',
            padding: '0.45rem 0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.45rem',
            fontSize: '0.78rem',
            fontWeight: 600,
            color: 'var(--text)',
            zIndex: 4,
          }}
        >
          <span style={{ color: 'var(--accent)' }}>✦</span>
          <span>Biosignals & Sensor Fusion</span>
        </motion.div>

        {/* Chip 2: Process Mining */}
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 5.2, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
          style={{
            position: 'absolute',
            bottom: '10%',
            left: '-6%',
            background: 'rgba(250, 246, 238, 0.92)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(45, 23, 83, 0.12)',
            boxShadow: '0 8px 24px rgba(45, 23, 83, 0.08)',
            borderRadius: '100px',
            padding: '0.45rem 0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.45rem',
            fontSize: '0.78rem',
            fontWeight: 600,
            color: 'var(--text)',
            zIndex: 4,
          }}
        >
          <span style={{ color: '#3a7050' }}>●</span>
          <span>Health Process Analytics</span>
        </motion.div>
      </motion.div>
    </div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const { meta, hero } = data;
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const containerRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };

  return (
    <section
      id="hero"
      ref={containerRef}
      onMouseMove={handleMouseMove}
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: '6rem',
        paddingBottom: '5rem',
      }}
    >
      <style>{`
        #hero-grid {
          display: grid;
          grid-template-columns: 1.15fr 0.85fr;
          gap: 4.5rem;
          align-items: center;
          width: 100%;
        }

        .quick-stats-strip {
          display: flex;
          align-items: center;
          gap: 2rem;
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 1px solid var(--border);
          flex-wrap: wrap;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
        }
        .stat-number {
          font-family: var(--font-heading);
          font-size: 1.55rem;
          font-weight: 700;
          color: var(--text);
          line-height: 1.1;
        }
        .stat-label {
          font-size: 0.78rem;
          color: var(--text-muted);
          font-weight: 500;
          letter-spacing: 0.02em;
        }

        @media (max-width: 960px) {
          #hero-grid {
            grid-template-columns: 1fr !important;
            gap: 3.5rem !important;
          }
          #hero-visual {
            order: -1;
            max-width: 340px !important;
            margin: 0 auto !important;
          }
        }

        @media (max-width: 600px) {
          #hero-visual {
            max-width: 280px !important;
          }
          #hero-grid {
            gap: 2.5rem !important;
          }
          .quick-stats-strip {
            gap: 1.5rem !important;
            margin-top: 2.5rem !important;
            padding-top: 1.5rem !important;
          }
        }
      `}</style>

      <div className="container">
        <div id="hero-grid">
          {/* Left: Text & Bio Content */}
          <div>
            {/* Editorial Status Badge */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0.05}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.6rem',
                padding: '0.4rem 1rem',
                borderRadius: '100px',
                background: 'rgba(247, 190, 161, 0.25)',
                border: '1px solid rgba(232, 149, 109, 0.4)',
                marginBottom: '1.25rem',
              }}
            >
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: 'var(--accent-dark)',
                  boxShadow: '0 0 8px var(--accent)',
                  animation: 'pulse-soft 2s infinite',
                }}
              />
              <span
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: 'var(--accent-dark)',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                {meta.title}
              </span>
            </motion.div>

            {/* Main Name Heading */}
            <motion.h1
              className="foil-shine"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0.15}
              style={{
                fontSize: 'clamp(3rem, 6.5vw, 5.8rem)',
                fontWeight: 700,
                lineHeight: 1.05,
                marginBottom: '1rem',
                letterSpacing: '-0.03em',
              }}
            >
              {meta.name}
            </motion.h1>

            {/* Kinetic Rotating Vibe Subtitle */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0.25}
              style={{
                fontSize: 'clamp(1.3rem, 3vw, 2.2rem)',
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                marginBottom: '1.5rem',
                lineHeight: 1.3,
                minHeight: '1.4em',
              }}
            >
              <RotatingText items={hero.rotating} />
            </motion.div>

            {/* "Currently" Focus Highlight */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0.35}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.65rem',
                background: 'rgba(255, 255, 255, 0.45)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(45, 23, 83, 0.08)',
                padding: '0.85rem 1.25rem',
                borderRadius: '16px',
                marginBottom: '2.5rem',
                maxWidth: '620px',
              }}
            >
              <span
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--accent)',
                  marginTop: '0.15rem',
                  flexShrink: 0,
                }}
              >
                ✦
              </span>
              <p
                style={{
                  fontSize: '0.92rem',
                  color: 'var(--text-muted)',
                  lineHeight: 1.6,
                  fontStyle: 'italic',
                }}
              >
                {meta.currently}
              </p>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0.45}
              style={{
                display: 'flex',
                gap: '0.85rem',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <MagneticButton strength={0.3}>
                <a
                  href={meta.resume}
                  className="btn btn-primary"
                  target="_blank"
                  rel="noreferrer"
                  data-cursor-hover
                >
                  Resume ↗
                </a>
              </MagneticButton>

              <MagneticButton strength={0.3}>
                <a
                  href="#projects"
                  className="btn btn-outline"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  data-cursor-hover
                >
                  Explore Projects ↓
                </a>
              </MagneticButton>

              <MagneticButton strength={0.3}>
                <a
                  href={meta.github}
                  className="btn btn-outline"
                  target="_blank"
                  rel="noreferrer"
                  data-cursor-hover
                >
                  GitHub
                </a>
              </MagneticButton>

              <MagneticButton strength={0.3}>
                <a
                  href={meta.linkedin}
                  className="btn btn-outline"
                  target="_blank"
                  rel="noreferrer"
                  data-cursor-hover
                >
                  LinkedIn
                </a>
              </MagneticButton>
            </motion.div>

            {/* Quick Metrics Strip */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0.55}
              className="quick-stats-strip"
            >
              <div className="stat-item">
                <span className="stat-number">13+</span>
                <span className="stat-label">Projects & Prototypes</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">VIT</span>
                <span className="stat-label">Integrated M.Tech CS</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">Health × AI</span>
                <span className="stat-label">Biosignals & Analytics</span>
              </div>
            </motion.div>
          </div>

          {/* Right: Bio-Tech Portal with Portrait */}
          <motion.div
            id="hero-visual"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <BioTechPortal mousePos={mousePos} />
          </motion.div>
        </div>

        {/* Cinematic Scroll Down Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          onClick={() => {
            document.getElementById('skills')?.scrollIntoView({ behavior: 'smooth' });
          }}
          data-cursor-hover
          style={{
            marginTop: '4rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.6rem',
            color: 'var(--text-faint)',
            fontSize: '0.8rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            padding: '0.4rem 0.8rem',
            borderRadius: '100px',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-faint)')}
        >
          <span>Scroll to explore</span>
          <motion.span
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{ display: 'inline-block' }}
          >
            ↓
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
}
